import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Card, ErrorNote, Spinner, ui } from '@/components/ui';
import { mfaService } from '@/services/api';
import { CodeInput } from './CodeInput';
import { RecoveryCodes } from './RecoveryCodes';
import { SetupAuthenticatorApp } from './SetupAuthenticatorApp';

export const mfaStatusKey = ['mfa', 'status'] as const;

type Mode = 'view' | 'setup' | 'new-codes' | 'disable';

// Account → Two-step verification.
export function SecurityCard() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: mfaStatusKey, queryFn: mfaService.status });
  const [mode, setMode] = useState<Mode>('view');
  const refresh = async () => {
    setMode('view');
    await qc.invalidateQueries({ queryKey: ['mfa'] });
  };

  return (
    <Card
      title="Two-step verification"
      actions={
        status.data?.enabled ? <Badge tone="green">On</Badge> : <Badge tone="amber">Off</Badge>
      }
    >
      {status.isPending ? <Spinner /> : null}
      <ErrorNote error={status.error} />
      {status.data && mode === 'setup' ? (
        <SetupAuthenticatorApp
          onDone={() => {
            toast.success('Two-step verification is on');
            void refresh();
          }}
          onCancel={() => {
            setMode('view');
          }}
        />
      ) : null}
      {status.data && (mode === 'new-codes' || mode === 'disable') ? (
        <CodeAction
          mode={mode}
          onDone={refresh}
          onCancel={() => {
            setMode('view');
          }}
        />
      ) : null}
      {status.data && mode === 'view' ? (
        status.data.enabled ? (
          <div className={ui.stack}>
            <p>
              Signing in asks for a code from your authenticator app after your password.{' '}
              {status.data.recoveryCodesLeft} recovery code
              {status.data.recoveryCodesLeft === 1 ? '' : 's'} left.
            </p>
            <div className={ui.row}>
              <Button
                variant="secondary"
                onClick={() => {
                  setMode('new-codes');
                }}
              >
                New recovery codes
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setMode('setup');
                }}
              >
                Use a different phone
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setMode('disable');
                }}
              >
                Turn off
              </Button>
            </div>
          </div>
        ) : (
          <div className={ui.stack}>
            <p>
              Protect your account with a code from your phone each time you sign in. Required for
              Fairvero staff using the admin area.
            </p>
            <div>
              <Button
                onClick={() => {
                  setMode('setup');
                }}
              >
                Set up authenticator app
              </Button>
            </div>
          </div>
        )
      ) : null}
    </Card>
  );
}

// Actions that need a current code: new recovery codes, turning it off.
function CodeAction({
  mode,
  onDone,
  onCancel,
}: {
  mode: 'new-codes' | 'disable';
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const disable = useMutation({
    mutationFn: () => mfaService.disable(code),
    onSuccess: async () => {
      toast.success('Two-step verification is off');
      await onDone();
    },
  });
  const newCodes = useMutation({ mutationFn: () => mfaService.newRecoveryCodes(code) });
  const run = mode === 'disable' ? disable : newCodes;
  if (newCodes.data)
    return <RecoveryCodes codes={newCodes.data.recoveryCodes} onDone={() => void onDone()} />;
  return (
    <form
      className={ui.stack}
      onSubmit={(e) => {
        e.preventDefault();
        run.mutate();
      }}
    >
      <p>
        {mode === 'disable'
          ? 'Enter a code from your app to turn two-step verification off. You won’t be able to open the admin area without it.'
          : 'Enter a code from your app. Your old recovery codes will stop working.'}
      </p>
      <CodeInput id="mfa-action-code" value={code} onChange={setCode} autoFocus />
      <ErrorNote error={run.error} />
      <div className={ui.row}>
        <Button
          type="submit"
          variant={mode === 'disable' ? 'danger' : 'primary'}
          disabled={run.isPending}
        >
          {mode === 'disable' ? 'Turn off' : 'Make new codes'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
